(function laborDayModule(globalScope) {
  'use strict';

  function toInteger(value, fallback) {
    var parsed = Number(value);
    return Number.isFinite(parsed) ? Math.trunc(parsed) : fallback;
  }

  function clamp(value, minimum, maximum) {
    return Math.min(Math.max(value, minimum), maximum);
  }

  function hasInventoryQuantity(value) {
    return value !== null
      && value !== undefined
      && !(typeof value === 'string' && value.trim() === '');
  }

  function calculateCampaignRemaining(input) {
    var manualRemaining = Math.max(0, toInteger(input.manualRemaining, 0));

    if (input.tracked && input.policy && input.policy !== 'deny') {
      return null;
    }

    if (input.available === false) {
      return 0;
    }

    if (input.mode === 'adjusted') {
      if (!input.tracked) {
        return null;
      }

      if (!hasInventoryQuantity(input.liveInventory)) {
        return manualRemaining;
      }

      var adjustedLiveInventory = Math.max(0, toInteger(input.liveInventory, 0));
      var inventoryAdjustment = toInteger(input.inventoryAdjustment, 0);
      return clamp(
        adjustedLiveInventory + inventoryAdjustment,
        0,
        adjustedLiveInventory
      );
    }

    if (input.mode !== 'reference' || !input.tracked) {
      return manualRemaining;
    }

    var liveInventory = Math.max(0, toInteger(input.liveInventory, 0));
    var inventoryReference = Math.max(0, toInteger(input.inventoryReference, 0));
    var campaignAllocation = Math.max(0, toInteger(input.campaignAllocation, 0));

    if (inventoryReference <= 0 || campaignAllocation <= 0) {
      return Math.min(liveInventory, manualRemaining);
    }

    var unitsSoldSinceReference = Math.max(0, inventoryReference - liveInventory);
    var campaignRemaining = campaignAllocation - unitsSoldSinceReference;

    return clamp(campaignRemaining, 0, Math.min(campaignAllocation, liveInventory));
  }

  function getStockPresentation(remaining, allocation, lowThreshold) {
    var safeRemaining = Math.max(0, toInteger(remaining, 0));
    var safeAllocation = Math.max(1, toInteger(allocation, 1));
    var safeThreshold = Math.max(0, toInteger(lowThreshold, 0));
    var state = 'available';

    if (safeRemaining === 0) {
      state = 'sold-out';
    } else if (safeThreshold > 0 && safeRemaining <= safeThreshold) {
      state = 'low';
    }

    return {
      state: state,
      percent: clamp(Math.round((safeRemaining / safeAllocation) * 100), 0, 100)
    };
  }

  function getCountdownParts(targetTime, currentTime) {
    var difference = Math.max(0, toInteger(targetTime, 0) - toInteger(currentTime, 0));
    var totalSeconds = Math.floor(difference / 1000);

    return {
      days: Math.floor(totalSeconds / 86400),
      hours: Math.floor((totalSeconds % 86400) / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60),
      seconds: totalSeconds % 60,
      expired: difference <= 0
    };
  }

  function findVariant(variants, selectedOptions) {
    if (!Array.isArray(variants) || !Array.isArray(selectedOptions)) {
      return null;
    }

    return variants.find(function findMatchingVariant(variant) {
      if (!Array.isArray(variant.options) || variant.options.length !== selectedOptions.length) {
        return false;
      }

      return variant.options.every(function optionMatches(option, index) {
        return option === selectedOptions[index];
      });
    }) || null;
  }

  function buildVideoEmbedUrl(provider, videoId) {
    var safeId = String(videoId || '');

    if (provider === 'youtube' && /^[A-Za-z0-9_-]{6,}$/.test(safeId)) {
      return 'https://www.youtube-nocookie.com/embed/' + safeId + '?autoplay=1&rel=0&playsinline=1';
    }

    if (provider === 'vimeo' && /^\d{5,}$/.test(safeId)) {
      return 'https://player.vimeo.com/video/' + safeId + '?autoplay=1';
    }

    return '';
  }

  function calculateTwoItemOffer(priceA, priceB, discountPercent) {
    var firstPrice = Math.max(0, toInteger(priceA, 0));
    var secondPrice = Math.max(0, toInteger(priceB, 0));
    var safeDiscount = clamp(toInteger(discountPercent, 8), 0, 100);
    var subtotal = firstPrice + secondPrice;
    var savings = Math.round((subtotal * safeDiscount) / 100);

    return {
      subtotal: subtotal,
      savings: savings,
      estimatedTotal: subtotal - savings,
      discountPercent: safeDiscount
    };
  }

  function normalizeVariantId(value) {
    var parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }

  function buildTwoItemCartItems(variantA, variantB) {
    var firstId = normalizeVariantId(variantA);
    var secondId = normalizeVariantId(variantB);

    if (!firstId || !secondId) return [];
    if (firstId === secondId) return [{ id: firstId, quantity: 2 }];
    return [
      { id: firstId, quantity: 1 },
      { id: secondId, quantity: 1 }
    ];
  }

  function normalizeRoutesRoot(value) {
    var route = String(value || '/');
    if (route.charAt(0) !== '/') route = '/' + route;
    if (route.charAt(route.length - 1) !== '/') route += '/';
    return route;
  }

  function addTwoItemsToCart(input) {
    var items = buildTwoItemCartItems(input.variantA, input.variantB);
    var fetchLike = input.fetchLike;
    if (items.length === 0) return Promise.reject(new Error('Choose two available products.'));
    if (typeof fetchLike !== 'function') return Promise.reject(new Error('Cart service is unavailable.'));

    return fetchLike(normalizeRoutesRoot(input.routesRoot) + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ items: items })
    }).then(function parseCartResponse(response) {
      return Promise.resolve(typeof response.json === 'function' ? response.json() : {})
        .catch(function ignoreInvalidJson() { return {}; })
        .then(function validateCartResponse(data) {
          if (!response.ok) {
            var message = data && (data.description || data.message)
              ? data.description || data.message
              : 'Unable to add both products. Please check availability and try again.';
            var error = new Error(message);
            error.status = response.status;
            throw error;
          }
          return data;
        });
    });
  }

  function formatMoneyCents(value, currency, locale) {
    var amount = Math.max(0, toInteger(value, 0)) / 100;
    try {
      return new Intl.NumberFormat(locale || 'en-US', {
        style: 'currency',
        currency: currency || 'USD'
      }).format(amount);
    } catch (error) {
      return amount.toFixed(2) + ' ' + (currency || 'USD');
    }
  }

  function calculateDiscountedPrice(priceCents, discountType, discountValue) {
    var originalPrice = Math.max(0, toInteger(priceCents, 0));
    var numericValue = Math.max(0, Number(discountValue) || 0);
    var savings = discountType === 'percent'
      ? Math.round((originalPrice * numericValue) / 100)
      : Math.round(numericValue * 100);
    savings = clamp(savings, 0, originalPrice);

    return {
      originalPrice: originalPrice,
      savings: savings,
      finalPrice: originalPrice - savings
    };
  }

  function renderCouponPrice(productRoot, priceCents) {
    if (!productRoot || typeof productRoot.querySelector !== 'function') return null;
    var coupon = productRoot.querySelector('[data-ld-coupon-price]');
    if (!coupon) return null;
    var finalPrice = coupon.querySelector('[data-ld-coupon-final]');
    if (!finalPrice) return null;
    var savings = coupon.querySelector('[data-ld-coupon-savings]');
    var result = calculateDiscountedPrice(
      priceCents,
      coupon.dataset.discountType,
      coupon.dataset.discountValue
    );
    var locale = typeof document !== 'undefined' && document.documentElement
      ? document.documentElement.lang
      : 'en-US';
    finalPrice.textContent = formatMoneyCents(
      result.finalPrice,
      coupon.dataset.currency || 'USD',
      locale
    );
    if (savings) {
      savings.textContent = formatMoneyCents(
        result.savings,
        coupon.dataset.currency || 'USD',
        locale
      );
    }
    coupon.hidden = false;
    return result;
  }

  function renderOfferCalculator(calculator) {
    var firstSelect = calculator.querySelector('[data-ld-offer-select="a"]');
    var secondSelect = calculator.querySelector('[data-ld-offer-select="b"]');
    var addButton = calculator.querySelector('[data-ld-offer-add]');
    if (!firstSelect || !secondSelect || !addButton) return null;

    if (calculator.dataset.ldOfferDefaulted !== 'true') {
      if (secondSelect.options && secondSelect.options.length > 1 && secondSelect.value === firstSelect.value) {
        secondSelect.selectedIndex = 1;
      }
      calculator.dataset.ldOfferDefaulted = 'true';
    }

    var firstOption = firstSelect.options && firstSelect.selectedIndex >= 0
      ? firstSelect.options[firstSelect.selectedIndex]
      : null;
    var secondOption = secondSelect.options && secondSelect.selectedIndex >= 0
      ? secondSelect.options[secondSelect.selectedIndex]
      : null;
    var firstPrice = firstOption ? firstOption.dataset.price : null;
    var secondPrice = secondOption ? secondOption.dataset.price : null;
    var valid = Boolean(normalizeVariantId(firstSelect.value) && normalizeVariantId(secondSelect.value));
    var offer = calculateTwoItemOffer(firstPrice, secondPrice, calculator.dataset.discountPercent);
    var locale = typeof document !== 'undefined' && document.documentElement
      ? document.documentElement.lang
      : 'en-US';
    var currency = calculator.dataset.currency || 'USD';
    var values = {
      '[data-ld-offer-subtotal]': offer.subtotal,
      '[data-ld-offer-savings]': offer.savings,
      '[data-ld-offer-total]': offer.estimatedTotal
    };

    Object.keys(values).forEach(function updateOfferValue(selector) {
      var node = calculator.querySelector(selector);
      if (node) node.textContent = formatMoneyCents(values[selector], currency, locale);
    });
    addButton.disabled = !valid;
    return offer;
  }

  function getInventoryViewModel(input) {
    var remaining = calculateCampaignRemaining(input);

    if (remaining === null) {
      return {
        remaining: null,
        state: 'untracked',
        percent: 0,
        label: input.untrackedLabel || 'In stock · Labor Day price ends soon',
        source: 'untracked'
      };
    }

    var presentation = getStockPresentation(
      remaining,
      input.stockMax,
      input.lowThreshold
    );
    var source = !input.tracked
      ? 'untracked'
      : input.mode === 'adjusted'
        && input.policy === 'deny'
        && hasInventoryQuantity(input.liveInventory)
        ? 'live'
        : 'manual';
    var label = remaining + ' left at this price';

    if (input.available === false) {
      label = 'Sold out';
    } else if (presentation.state === 'low') {
      label = 'Only ' + remaining + ' left at this price';
    } else if (presentation.state === 'sold-out') {
      label = 'Sold out at this price';
    }

    return {
      remaining: remaining,
      state: presentation.state,
      percent: presentation.percent,
      label: label,
      source: source
    };
  }

  function getInventorySourceCopy(source) {
    if (source === 'live') {
      return 'Updates on refresh · Confirmed at checkout';
    }
    if (source === 'untracked') {
      return 'Current price available while inventory lasts';
    }
    return 'Limited quantity at this price';
  }

  function renderInventoryState(inventory, viewModel) {
    if (!inventory || !viewModel) return viewModel;
    var isCard = typeof inventory.hasAttribute === 'function'
      && inventory.hasAttribute('data-ld-card-inventory');
    var classPrefix = isCard ? 'fg-ld__card-stock--' : 'fg-ld__inventory--';
    ['available', 'low', 'sold-out', 'untracked'].forEach(function removeInventoryState(state) {
      inventory.classList.remove(classPrefix + state);
    });
    inventory.classList.add(classPrefix + viewModel.state);
    inventory.dataset.stockSource = viewModel.source;

    var stockLabel = inventory.querySelector('[data-ld-stock-label]');
    var stockBar = inventory.querySelector('[data-ld-stock-bar]');
    var stockSourceCopy = inventory.querySelector('[data-ld-stock-source-copy]');
    if (stockLabel) stockLabel.textContent = viewModel.label;
    if (stockBar) stockBar.style.width = viewModel.percent + '%';
    if (stockSourceCopy) stockSourceCopy.textContent = getInventorySourceCopy(viewModel.source);
    return viewModel;
  }

  function applyInventorySettings(inventory, settings) {
    if (!inventory || !settings) return;
    if (settings.mode === 'manual' || settings.mode === 'live_adjusted') {
      inventory.dataset.mode = settings.mode;
    }
    var values = {
      adjustment: settings.adjustment,
      manualRemaining: settings.manualRemaining,
      stockMax: settings.stockMax,
      lowThreshold: settings.lowThreshold
    };
    Object.keys(values).forEach(function setInventorySetting(key) {
      if (values[key] !== null && values[key] !== undefined && values[key] !== '') {
        inventory.dataset[key] = String(toInteger(values[key], 0));
      }
    });
  }

  function inventoryViewFromDataset(inventory, overrides) {
    var input = overrides || {};
    return getInventoryViewModel({
      mode: inventory.dataset.mode === 'live_adjusted' ? 'adjusted' : 'manual',
      tracked: input.tracked !== undefined ? Boolean(input.tracked) : inventory.dataset.tracked === 'true',
      policy: input.policy !== undefined ? input.policy : inventory.dataset.policy,
      available: input.available !== undefined ? Boolean(input.available) : inventory.dataset.available !== 'false',
      liveInventory: input.liveInventory !== undefined ? input.liveInventory : inventory.dataset.liveInventory,
      inventoryAdjustment: inventory.dataset.adjustment,
      manualRemaining: inventory.dataset.manualRemaining,
      stockMax: inventory.dataset.stockMax,
      lowThreshold: inventory.dataset.lowThreshold,
      untrackedLabel: inventory.dataset.untrackedLabel
    });
  }

  function applyInventoryEditorConfig(page) {
    if (!page || typeof page.querySelector !== 'function') return false;
    var configNode = page.querySelector('[data-ld-inventory-config]');
    if (!configNode) return false;
    var config = null;
    try {
      config = JSON.parse(configNode.textContent || '{}');
    } catch (error) {
      return false;
    }

    var productSettings = {};
    (Array.isArray(config.products) ? config.products : []).forEach(function indexProductSettings(settings) {
      if (settings && settings.handle) productSettings[String(settings.handle)] = settings;
    });

    Array.prototype.slice.call(page.querySelectorAll(
      '[data-ld-inventory][data-product-handle], [data-ld-card-inventory][data-product-handle]'
    )).forEach(function configureInventory(inventory) {
      var productHandle = String(inventory.dataset.productHandle || '');
      var settings = inventory.dataset.featuredInventory === '35k' && config.featured
        ? config.featured
        : productSettings[productHandle];
      if (settings) applyInventorySettings(inventory, settings);

      var productRoot = inventory.closest('[data-ld-product-form]');
      var variantsNode = productRoot && productRoot.querySelector('[data-ld-variants]');
      var variantInput = productRoot && productRoot.querySelector('[data-ld-variant-id]');
      var overrides = null;
      if (variantsNode && variantInput) {
        try {
          var variants = JSON.parse(variantsNode.textContent || '[]');
          var selectedVariant = variants.find(function findCurrentVariant(variant) {
            return String(variant.id) === String(variantInput.value);
          });
          if (selectedVariant) {
            overrides = {
              tracked: selectedVariant.tracked,
              policy: selectedVariant.inventoryPolicy,
              available: selectedVariant.available,
              liveInventory: selectedVariant.inventoryQuantity
            };
          }
        } catch (error) {
          // Fall back to the server-rendered inventory attributes.
        }
      }
      renderInventoryState(inventory, inventoryViewFromDataset(inventory, overrides));
    });
    return true;
  }

  function productMatchesFilter(category, selectedFilter) {
    var normalizedFilter = String(selectedFilter || '').trim().toLowerCase();
    var normalizedCategory = String(category || '').trim().toLowerCase();
    return normalizedFilter === 'all' || normalizedCategory === normalizedFilter;
  }

  function boot(rootDocument) {
    var documentLike = rootDocument || (typeof document !== 'undefined' ? document : null);
    if (!documentLike || typeof documentLike.querySelectorAll !== 'function') {
      return 0;
    }

    var initializedCount = 0;
    var pages = Array.prototype.slice.call(
      documentLike.querySelectorAll('[data-labor-day-page]')
    );

    pages.forEach(function initializePage(page) {
      if (page.dataset.ldInitialized === 'true') {
        return;
      }

      page.dataset.ldInitialized = 'true';
      page._fgLaborDayTimers = [];
      initializedCount += 1;

      applyInventoryEditorConfig(page);

      Array.prototype.slice.call(page.querySelectorAll('[data-ld-countdown]')).forEach(function setupCountdown(countdown) {
        var targetTime = Date.parse(countdown.dataset.endTime || '');
        if (!Number.isFinite(targetTime)) {
          countdown.hidden = true;
          return;
        }

        var timerId = null;
        var renderCountdown = function renderCountdown() {
          var parts = getCountdownParts(targetTime, Date.now());
          var values = {
            '[data-days]': parts.days,
            '[data-hours]': parts.hours,
            '[data-minutes]': parts.minutes,
            '[data-seconds]': parts.seconds
          };

          Object.keys(values).forEach(function updateCountdownPart(selector) {
            var node = countdown.querySelector(selector);
            if (node) {
              node.textContent = String(values[selector]).padStart(2, '0');
            }
          });

          countdown.dataset.expired = parts.expired ? 'true' : 'false';
          Array.prototype.slice.call(countdown.querySelectorAll('[data-ld-countdown-part]')).forEach(function toggleCountdownPart(part) {
            part.hidden = parts.expired;
          });
          var expiredMessage = countdown.querySelector('[data-ld-countdown-expired]');
          if (expiredMessage) expiredMessage.hidden = !parts.expired;
          if (parts.expired && timerId !== null && typeof clearInterval === 'function') {
            clearInterval(timerId);
            timerId = null;
          }
        };

        renderCountdown();
        if (countdown.dataset.expired !== 'true' && typeof setInterval === 'function') {
          timerId = setInterval(renderCountdown, 1000);
          page._fgLaborDayTimers.push(timerId);
        }
      });

      Array.prototype.slice.call(page.querySelectorAll('[data-ld-product-form]')).forEach(function prepareProductForm(productRoot) {
        var variantInput = productRoot.querySelector('[data-ld-variant-id]');
        if (variantInput) variantInput.disabled = false;
        Array.prototype.slice.call(productRoot.querySelectorAll('[data-ld-enhanced-variants]')).forEach(function revealEnhancedSelectors(selectors) {
          selectors.hidden = false;
        });
        var atcLabel = productRoot.querySelector('[data-ld-atc-label]');
        if (atcLabel && !atcLabel.dataset.availableLabel) {
          atcLabel.dataset.availableLabel = atcLabel.textContent.trim();
        }
      });

      Array.prototype.slice.call(page.querySelectorAll('[data-ld-offer-calculator]')).forEach(function prepareOfferCalculator(calculator) {
        renderOfferCalculator(calculator);
      });

      var availableCards = Array.prototype.slice.call(page.querySelectorAll('[data-ld-product-card]'));
      Array.prototype.slice.call(page.querySelectorAll('[data-ld-filter]')).forEach(function hideEmptyProductFilter(button) {
        var filter = button.dataset.ldFilter;
        if (filter === 'all') return;
        button.hidden = !availableCards.some(function categoryExists(card) {
          return productMatchesFilter(card.dataset.category, filter);
        });
      });

      Array.prototype.slice.call(page.querySelectorAll('[data-ld-video-dialog]')).forEach(function prepareVideoDialog(dialog) {
        if (dialog.dataset.ldDialogInitialized === 'true') return;
        dialog.dataset.ldDialogInitialized = 'true';

        var clearVideo = function clearVideo() {
          var host = dialog.querySelector('[data-ld-video-host]');
          if (host) {
            if (typeof host.replaceChildren === 'function') host.replaceChildren();
            else host.innerHTML = '';
          }
          if (dialog._fgLaborDayOpener && typeof dialog._fgLaborDayOpener.focus === 'function') {
            dialog._fgLaborDayOpener.focus();
          }
          dialog._fgLaborDayOpener = null;
        };

        dialog.addEventListener('close', clearVideo);
        dialog.addEventListener('click', function closeOnBackdrop(event) {
          if (event.target === dialog && typeof dialog.close === 'function') dialog.close();
        });
      });

      page.addEventListener('click', function handleCampaignClick(event) {
        var target = event.target;
        if (!target || typeof target.closest !== 'function') {
          return;
        }

        var copyCodeButton = target.closest('[data-ld-copy-code]');
        if (copyCodeButton) {
          if (typeof event.preventDefault === 'function') event.preventDefault();
          var code = copyCodeButton.dataset.ldCopyCode || '';
          var copyLabel = copyCodeButton.querySelector('[data-ld-copy-label]');
          var copyPromise = null;
          if (code && globalScope && globalScope.navigator && globalScope.navigator.clipboard && typeof globalScope.navigator.clipboard.writeText === 'function') {
            copyPromise = globalScope.navigator.clipboard.writeText(code);
          } else if (code && documentLike && typeof documentLike.createElement === 'function') {
            var temporaryInput = documentLike.createElement('textarea');
            temporaryInput.value = code;
            temporaryInput.setAttribute('readonly', '');
            temporaryInput.style.position = 'fixed';
            temporaryInput.style.opacity = '0';
            documentLike.body.appendChild(temporaryInput);
            temporaryInput.select();
            try {
              documentLike.execCommand('copy');
              copyPromise = Promise.resolve();
            } catch (error) {
              copyPromise = Promise.reject(error);
            }
            documentLike.body.removeChild(temporaryInput);
          }
          if (copyPromise && typeof copyPromise.then === 'function') {
            copyPromise.then(function showCopiedState() {
              if (copyLabel) copyLabel.textContent = 'Copied';
              copyCodeButton.dataset.copied = 'true';
            }).catch(function showCopyFallback() {
              if (copyLabel) copyLabel.textContent = 'Select code';
            });
          }
          return;
        }

        var offerButton = target.closest('[data-ld-offer-add]');
        if (offerButton) {
          if (typeof event.preventDefault === 'function') event.preventDefault();
          var calculator = offerButton.closest('[data-ld-offer-calculator]');
          if (!calculator || offerButton.dataset.submitting === 'true') return;
          var firstOfferSelect = calculator.querySelector('[data-ld-offer-select="a"]');
          var secondOfferSelect = calculator.querySelector('[data-ld-offer-select="b"]');
          var offerStatus = calculator.querySelector('[data-ld-offer-status]');
          if (!firstOfferSelect || !secondOfferSelect) return;

          offerButton.dataset.submitting = 'true';
          offerButton.disabled = true;
          offerButton.dataset.defaultLabel = offerButton.dataset.defaultLabel || offerButton.textContent.trim();
          offerButton.textContent = 'Adding both products…';
          if (offerStatus) offerStatus.textContent = '';
          var routesRoot = globalScope && globalScope.Shopify && globalScope.Shopify.routes
            ? globalScope.Shopify.routes.root
            : '/';
          var fetchLike = globalScope && typeof globalScope.fetch === 'function'
            ? globalScope.fetch.bind(globalScope)
            : null;

          addTwoItemsToCart({
            variantA: firstOfferSelect.value,
            variantB: secondOfferSelect.value,
            routesRoot: routesRoot,
            fetchLike: fetchLike
          }).then(function openCart() {
            if (globalScope && globalScope.location && typeof globalScope.location.assign === 'function') {
              globalScope.location.assign(normalizeRoutesRoot(routesRoot) + 'cart');
            }
          }).catch(function showOfferError(error) {
            offerButton.dataset.submitting = 'false';
            offerButton.disabled = false;
            offerButton.textContent = offerButton.dataset.defaultLabel;
            if (offerStatus) offerStatus.textContent = error.message;
          });
          return;
        }

        var scrollButton = target.closest('[data-ld-scroll-to]');
        if (scrollButton) {
          var scrollTarget = documentLike.querySelector(scrollButton.dataset.ldScrollTo);
          if (scrollTarget && typeof scrollTarget.scrollIntoView === 'function') {
            var reduceMotion = globalScope && typeof globalScope.matchMedia === 'function'
              ? globalScope.matchMedia('(prefers-reduced-motion: reduce)').matches
              : false;
            scrollTarget.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
          }
          return;
        }

        var filterButton = target.closest('[data-ld-filter]');
        if (filterButton) {
          var selectedFilter = filterButton.dataset.ldFilter;
          Array.prototype.slice.call(page.querySelectorAll('[data-ld-filter]')).forEach(function updateFilterButton(button) {
            var selected = button === filterButton;
            button.classList.toggle('is-active', selected);
            button.setAttribute('aria-pressed', selected ? 'true' : 'false');
          });
          Array.prototype.slice.call(page.querySelectorAll('[data-ld-product-card]')).forEach(function updateCard(card) {
            card.hidden = !productMatchesFilter(card.dataset.category, selectedFilter);
          });
          return;
        }

        var videoButton = target.closest('[data-ld-video-open]');
        if (videoButton) {
          var videoDialog = page.querySelector('[data-ld-video-dialog]');
          var videoHost = videoDialog && videoDialog.querySelector('[data-ld-video-host]');
          var embedUrl = buildVideoEmbedUrl(
            videoButton.dataset.videoProvider,
            videoButton.dataset.videoId
          );

          if (videoDialog && videoHost && embedUrl && typeof videoDialog.showModal === 'function' && typeof documentLike.createElement === 'function') {
            if (typeof event.preventDefault === 'function') event.preventDefault();
            var iframe = documentLike.createElement('iframe');
            iframe.src = embedUrl;
            iframe.title = videoButton.getAttribute('aria-label') || 'Creator review';
            iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
            iframe.allowFullscreen = true;
            iframe.referrerPolicy = 'strict-origin-when-cross-origin';
            if (typeof videoHost.replaceChildren === 'function') {
              videoHost.replaceChildren(iframe);
            } else {
              videoHost.innerHTML = '';
              videoHost.appendChild(iframe);
            }
            videoDialog._fgLaborDayOpener = videoButton;
            videoDialog.showModal();
            var dialogClose = videoDialog.querySelector('[data-ld-video-close]');
            if (dialogClose && typeof dialogClose.focus === 'function') dialogClose.focus();
          }
          return;
        }

        var closeButton = target.closest('[data-ld-video-close]');
        var activeDialog = closeButton ? closeButton.closest('[data-ld-video-dialog]') : null;
        if (activeDialog) {
          if (typeof activeDialog.close === 'function') {
            activeDialog.close();
          } else {
            activeDialog.removeAttribute('open');
          }
        }
      });

      page.addEventListener('change', function handleCampaignChange(event) {
        var optionSelect = event.target;
        if (!optionSelect || typeof optionSelect.matches !== 'function') {
          return;
        }

        if (optionSelect.matches('[data-ld-offer-select]')) {
          var offerCalculator = optionSelect.closest('[data-ld-offer-calculator]');
          if (offerCalculator) renderOfferCalculator(offerCalculator);
          return;
        }

        if (!optionSelect.matches('[data-option-select]')) return;

        var productRoot = optionSelect.closest('[data-ld-product-form]');
        if (!productRoot) {
          return;
        }

        var variantScript = productRoot.querySelector('[data-ld-variants]');
        var variants = [];
        try {
          variants = JSON.parse(variantScript ? variantScript.textContent : '[]');
        } catch (error) {
          return;
        }

        var selectedOptions = Array.prototype.slice.call(
          productRoot.querySelectorAll('[data-option-select]')
        ).map(function selectedOptionValue(select) {
          return select.value;
        });
        var variant = findVariant(variants, selectedOptions);
        if (!variant) {
          var staleVariantInput = productRoot.querySelector('[data-ld-variant-id]');
          var staleAtc = productRoot.querySelector('[data-ld-atc]');
          var staleAtcLabel = productRoot.querySelector('[data-ld-atc-label]');
          var staleComparePrice = productRoot.querySelector('[data-ld-compare-price]');
          var staleSaving = productRoot.querySelector('[data-ld-saving]');
          var staleInventory = productRoot.querySelector('[data-ld-inventory]');
          var staleCoupon = productRoot.querySelector('[data-ld-coupon-price]');
          if (staleVariantInput) staleVariantInput.disabled = true;
          if (staleAtc) staleAtc.disabled = true;
          if (staleAtcLabel) staleAtcLabel.textContent = 'Unavailable combination';
          if (staleComparePrice) staleComparePrice.hidden = true;
          if (staleSaving) staleSaving.hidden = true;
          if (staleCoupon) staleCoupon.hidden = true;
          if (staleInventory) {
            staleInventory.classList.remove(
              'fg-ld__inventory--available',
              'fg-ld__inventory--low',
              'fg-ld__inventory--sold-out',
              'fg-ld__inventory--untracked'
            );
            staleInventory.classList.add('fg-ld__inventory--untracked');
            var staleStockLabel = staleInventory.querySelector('[data-ld-stock-label]');
            var staleStockBar = staleInventory.querySelector('[data-ld-stock-bar]');
            if (staleStockLabel) staleStockLabel.textContent = 'Choose an available combination';
            if (staleStockBar) staleStockBar.style.width = '0%';
          }
          return;
        }

        var variantInput = productRoot.querySelector('[data-ld-variant-id]');
        var price = productRoot.querySelector('[data-ld-price]');
        var comparePrice = productRoot.querySelector('[data-ld-compare-price]');
        var compareWrap = productRoot.querySelector('[data-ld-compare-wrap]');
        var saving = productRoot.querySelector('[data-ld-saving]');
        var atc = productRoot.querySelector('[data-ld-atc]');
        var atcLabel = productRoot.querySelector('[data-ld-atc-label]');
        var atcPrice = productRoot.querySelector('[data-ld-atc-price]');

        if (variantInput) {
          variantInput.disabled = false;
          variantInput.value = variant.id;
        }
        if (price) price.textContent = variant.priceText;
        if (atcPrice) atcPrice.textContent = variant.priceText;
        renderCouponPrice(productRoot, variant.price);

        var hasComparePrice = Number(variant.compareAtPrice) > Number(variant.price);
        if (comparePrice) {
          comparePrice.textContent = hasComparePrice ? variant.compareAtPriceText : '';
        }
        if (compareWrap) compareWrap.hidden = !hasComparePrice;
        if (saving) {
          saving.hidden = !hasComparePrice;
          saving.textContent = hasComparePrice ? 'Save ' + variant.savingText : '';
        }
        var inventory = productRoot.querySelector('[data-ld-inventory]');
        var viewModel = null;
        if (inventory) {
          viewModel = getInventoryViewModel({
            mode: inventory.dataset.mode === 'live_adjusted' ? 'adjusted' : 'manual',
            tracked: Boolean(variant.tracked),
            policy: variant.inventoryPolicy,
            available: Boolean(variant.available),
            liveInventory: variant.inventoryQuantity,
            inventoryAdjustment: inventory.dataset.adjustment,
            manualRemaining: inventory.dataset.manualRemaining,
            stockMax: inventory.dataset.stockMax,
            lowThreshold: inventory.dataset.lowThreshold,
            untrackedLabel: inventory.dataset.untrackedLabel
          });
          renderInventoryState(inventory, viewModel);
        }

        if (atc) atc.disabled = !variant.available;
        if (atcLabel) {
          atcLabel.textContent = variant.available
            ? atcLabel.dataset.availableLabel || 'Add to cart'
            : 'Sold out';
        }
      });
    });

    return initializedCount;
  }

  var api = {
    calculateCampaignRemaining: calculateCampaignRemaining,
    getStockPresentation: getStockPresentation,
    getCountdownParts: getCountdownParts,
    findVariant: findVariant,
    buildVideoEmbedUrl: buildVideoEmbedUrl,
    calculateDiscountedPrice: calculateDiscountedPrice,
    renderCouponPrice: renderCouponPrice,
    applyInventoryEditorConfig: applyInventoryEditorConfig,
    calculateTwoItemOffer: calculateTwoItemOffer,
    buildTwoItemCartItems: buildTwoItemCartItems,
    addTwoItemsToCart: addTwoItemsToCart,
    renderOfferCalculator: renderOfferCalculator,
    getInventoryViewModel: getInventoryViewModel,
    productMatchesFilter: productMatchesFilter,
    boot: boot
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  if (globalScope) {
    globalScope.FogattiLaborDay = api;
  }

  if (typeof document !== 'undefined' && (!globalScope || !globalScope.__FogattiLaborDayListenersBound)) {
    if (globalScope) globalScope.__FogattiLaborDayListenersBound = true;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function initializeLaborDayPage() {
        boot(document);
      });
    } else {
      boot(document);
    }

    document.addEventListener('shopify:section:load', function reinitializeLaborDaySection(event) {
      var target = event && event.target;
      if (target && typeof target.querySelector === 'function') {
        var hasInventoryConfig = (typeof target.matches === 'function' && target.matches('.shopify-section--labor-day-inventory-config'))
          || Boolean(target.querySelector('[data-ld-inventory-config]'));
        if (hasInventoryConfig) {
          var campaignPage = typeof target.closest === 'function' ? target.closest('[data-labor-day-page]') : null;
          if (campaignPage) applyInventoryEditorConfig(campaignPage);
          return;
        }
      }
      boot(document);
    });

    document.addEventListener('shopify:section:unload', function cleanupLaborDaySection(event) {
      var target = event && event.target;
      if (!target || typeof target.querySelectorAll !== 'function') return;
      var pages = [];
      if (typeof target.matches === 'function' && target.matches('[data-labor-day-page]')) pages.push(target);
      pages = pages.concat(Array.prototype.slice.call(target.querySelectorAll('[data-labor-day-page]')));
      pages.forEach(function clearPageTimers(page) {
        (page._fgLaborDayTimers || []).forEach(function clearTimer(timerId) {
          if (typeof clearInterval === 'function') clearInterval(timerId);
        });
        page._fgLaborDayTimers = [];
      });
    });
  }
})(typeof window !== 'undefined' ? window : globalThis);
